import { useQuery } from "@apollo/client";
import { Box, styled } from "@mui/material";
import {
  ReactElement,
  RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useParams } from "react-router-dom";

import {
  CampaignStatus,
  GetCampaignForViewQuery,
  GetCampaignForViewQueryVariables,
  IntegrationServiceType,
} from "~frontend/__generated__/graphql";
import ErrorCard from "~frontend/components/ErrorCard";
import { FlexBox } from "~frontend/components/FlexBox";
import Loading from "~frontend/components/Loading";
import PageContainer from "~frontend/components/PageContainer";
import Redirect from "~frontend/components/Redirect";
import { GET_CAMPAIGN_FOR_VIEW } from "~frontend/graphql/campaigns";
import { useIntegrations } from "~frontend/hooks/useIntegrations";
import { useBreadcrumbs } from "~frontend/providers/BreadcrumbsProvider";
import { useFeatureFlag } from "~frontend/providers/LoggedInUserProvider/context";
import {
  useSubaccount,
  useSubaccountAdminConfig,
} from "~frontend/providers/SubaccountProvider";
import PartialSendEvaluationResults from "~frontend/routes/subaccounts/campaignView/PartialSendEvaluationResults";
import theme, { palette } from "~frontend/theme";
import { jobInProgress } from "~frontend/util/jobs";

import { CampaignDetails } from "./CampaignDetails";
import CampaignEventsTable from "./CampaignEventsTable";
import CampaignInitialsStatusTable from "./CampaignInitialsStatusTable";
import { CampaignPreviewMessagesTable } from "./CampaignPreviewMessagesTable";
import { CampaignViewAccordion } from "./CampaignViewAccordion";
import { Exports } from "./Exports";
import { TagUsage } from "./TagUsage";
import { Texters } from "./Texters";
import TopActionButtons from "./TopActionButtons";
import { CurrentAndPastVariantStatistics } from "./VariantStatistics";
import { shouldShowClicks } from "./shouldShowClicks";
import DetailStatsDrawer from "./stats/DetailStatsDrawer";
import TopLineStats from "./stats/TopLineStats";
import { useCampaignStats } from "./stats/useCampaignStats";

export const SectionWrapper = styled(Box)({
  backgroundColor: palette.lightestGray,
  padding: `${theme.spacing(4)} ${theme.spacing(7)}`,
});

export function CampaignView(): ReactElement {
  const { subaccountLink } = useSubaccount()!;
  const subaccount = useSubaccountAdminConfig()!;
  const messagingAllowed = useFeatureFlag("messagingAllowed");

  const { campaignShortId: maybeCampaignShortId } = useParams<{
    campaignShortId: string;
  }>();
  const campaignShortId = parseInt(maybeCampaignShortId!);

  const { error, loading, data, startPolling, stopPolling } = useQuery<
    GetCampaignForViewQuery,
    GetCampaignForViewQueryVariables
  >(GET_CAMPAIGN_FOR_VIEW, {
    variables: {
      input: {
        shortId: campaignShortId,
      },
    },
    fetchPolicy: "network-only",
  });

  const stats = useCampaignStats(campaignShortId);

  const {
    error: integrationsError,
    loading: integrationsLoading,
    hasIntegration: hasActBlueIntegration,
  } = useIntegrations({ service: IntegrationServiceType.ActBlueWebhook });

  const exportJobStatus = data?.getCampaignByShortId?.campaignExportJob?.status;
  const exportRepliesJobStatus =
    data?.getCampaignByShortId?.campaignRepliesExportJob?.status;

  useEffect(() => {
    const status = data?.getCampaignByShortId.status;
    if (
      status === CampaignStatus.InitialSendRunning ||
      (exportJobStatus && jobInProgress({ status: exportJobStatus })) ||
      (exportRepliesJobStatus &&
        jobInProgress({ status: exportRepliesJobStatus }))
    ) {
      startPolling(15 * 1000);
    } else if (status === CampaignStatus.InitialSendCanceling) {
      startPolling(1 * 1000);
    } else {
      startPolling(60 * 1000);
    }

    return () => stopPolling();
  }, [
    data?.getCampaignByShortId.status,
    exportJobStatus,
    exportRepliesJobStatus,
    startPolling,
    stopPolling,
  ]);

  useBreadcrumbs([
    {
      label: "Campaigns",
      path: subaccountLink("/campaigns"),
    },
    ...(data?.getCampaignByShortId
      ? [
          {
            label: data.getCampaignByShortId.name,
            path: subaccountLink(`/campaigns/${campaignShortId}`),
          },
        ]
      : []),
  ]);

  const [sendStatusOpen, setSendStatusOpen] = useState(false);
  const sendStatusAnchor = useRef<HTMLAnchorElement>(null);

  const revealSendStatus = useCallback(() => {
    setSendStatusOpen(true);

    // Delay scroll so that the accordion has time to expand
    setTimeout(() => {
      sendStatusAnchor.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 250);
  }, [setSendStatusOpen]);

  // Can delete once users are acclimated to the new Exports drawer
  const [exportsOpen, setExportsOpen] = useState(false);
  const exportsRef: RefObject<HTMLDivElement | null> =
    useRef<HTMLDivElement>(null);
  const revealExports = useCallback(() => {
    setExportsOpen(true);

    // Delay scroll so that the accordion has time to expand
    setTimeout(() => {
      exportsRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 250);
  }, [setExportsOpen]);

  if (loading || integrationsLoading) {
    return <Loading />;
  }

  if (error || integrationsError) {
    return <ErrorCard errors={[error || integrationsError]} />;
  }

  const campaign = data!.getCampaignByShortId;

  if (!campaign.startedAt) {
    return (
      <Redirect
        to={subaccountLink(`/campaigns/${campaign.shortId}/edit/basics`)}
      />
    );
  }

  const showClicks =
    campaign.scriptVariants.some(variant =>
      shouldShowClicks(variant, subaccount)
    ) ||
    (stats.state === "loaded" && stats.data.clickCount > 0);

  const showPartialSendResults = campaign.partialSendEvaluations.length > 0;

  return (
    <PageContainer
      maxWidth="lg"
      style={{
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(2),
        marginBottom: theme.spacing(6),
      }}
    >
      <FlexBox justifyContent="space-between" alignItems="center" gap={5}>
        {!messagingAllowed ? (
          <Box
            sx={{
              padding: "10px",
              backgroundColor: palette.lightOrange,
              width: "100%",
            }}
          >
            <strong>Warning:</strong> This account cannot send texts at this
            time. Please reach out to{" "}
            <a
              style={{ color: palette.blue, textDecorationColor: palette.blue }}
              href="mailto:support@scaletowin.com"
            >
              support@scaletowin.com
            </a>
            .
          </Box>
        ) : (
          <div />
        )}

        <TopActionButtons campaign={campaign} handleExport={revealExports} />
      </FlexBox>

      <FlexBox justifyContent="space-between" gap={5}>
        <CampaignDetails
          campaign={campaign}
          stats={stats}
          hasActBlueIntegration={hasActBlueIntegration}
          revealSendStatus={revealSendStatus}
        />

        <FlexBox direction="column" sx={{ width: "510px" }}>
          <Box
            sx={{
              padding: 4,
              backgroundColor: palette.white,
              borderRadius: "4px",
              boxShadow: "0px 1px 5px 0px rgba(106, 103, 131, 0.20)",
            }}
          >
            <TopLineStats
              stats={stats}
              showFundraisingFields={hasActBlueIntegration}
              showClicks={showClicks}
              usingOptInStrategy={campaign.usingOptInStrategy}
            />
            <DetailStatsDrawer
              stats={stats}
              showFundraisingFields={hasActBlueIntegration}
              showClicks={showClicks}
            />
          </Box>
        </FlexBox>
      </FlexBox>

      <CurrentAndPastVariantStatistics
        stats={stats}
        campaign={campaign}
        showFundraisingFields={hasActBlueIntegration}
      />
      {showPartialSendResults && (
        <CampaignViewAccordion
          header="Partial Send Results"
          sx={{ backgroundColor: palette.white, mt: 0 }}
        >
          <PartialSendEvaluationResults campaign={campaign} />
        </CampaignViewAccordion>
      )}

      <Texters campaign={campaign} />

      <TagUsage tagUsage={campaign.tagUsage} />

      <Box ref={exportsRef}>
        <Exports
          campaign={campaign}
          expanded={exportsOpen}
          // This prop is passed down to CampaignViewAccordion.
          // It fires when the Accordion component experiences a change event (when the header is clicked).
          // We should remove it when we phase out the top-of-page Export button.
          onExportChange={() => {
            setExportsOpen(false);
          }}
        ></Exports>
      </Box>

      <CampaignViewAccordion
        header="Sending Status"
        sx={{ backgroundColor: palette.white, mt: 0 }}
        expanded={sendStatusOpen}
        setExpanded={setSendStatusOpen}
      >
        <>
          <a id="send-status" href="#send-status" ref={sendStatusAnchor} />
          <FlexBox direction="row" gap={3}>
            <Box
              sx={{
                flex: "1",
                backgroundColor: palette.lightestGray,
                padding: 3,
              }}
            >
              <CampaignInitialsStatusTable
                initialSendFailures={campaign.initialSendFailures || []}
              />
            </Box>
            <Box
              sx={{
                flex: "1",
                backgroundColor: palette.lightestGray,
                padding: 3,
              }}
            >
              <CampaignPreviewMessagesTable campaignShortId={campaignShortId} />
            </Box>
          </FlexBox>
        </>
      </CampaignViewAccordion>

      <CampaignViewAccordion
        header="Event Log"
        sx={{ backgroundColor: palette.white, pb: 0 }}
      >
        <CampaignEventsTable campaign={campaign} />
      </CampaignViewAccordion>
    </PageContainer>
  );
}
